import { z } from "zod";
import { CARD_LANGUAGES, CardLanguage, PriceQuote } from "../types";
import type { CachedPrices, CardRef } from "./types";

// CachedPrices <-> a `card_prices` row (SPECS D20). The cache is shared by every account, so
// loaded rows are checked before use and a row that doesn't look like prices is ignored
// (04 FR-22e).

export interface PriceRow {
  language: CardLanguage;
  card_id: string;
  prices: unknown;
  fetched_at: string;
}

const figure = z.number().finite().min(0).optional();

const quoteSchema = z.object({
  source: z.enum(["cardmarket", "tcgplayer"]),
  currency: z.enum(["EUR", "USD"]),
  variant: z.string().min(1).max(50),
  trend: figure,
  avg: figure,
  low: figure,
  avg1: figure,
  avg7: figure,
  avg30: figure,
  market: figure,
  updatedAt: z.string().max(40),
  via: z.enum(["tcgdex", "pokemontcg", "ptcgdata"]).optional(),
  printLanguage: z.enum(CARD_LANGUAGES as [CardLanguage, ...CardLanguage[]]).optional(),
});

const rowSchema = z.object({
  language: z.enum(CARD_LANGUAGES as [CardLanguage, ...CardLanguage[]]),
  card_id: z.string().min(1).max(100),
  prices: z.array(quoteSchema).max(20),
  fetched_at: z.string().refine((s) => !Number.isNaN(Date.parse(s))),
});

/** null for a row that isn't valid cached prices. */
export function rowToCachedPrices(row: PriceRow): CachedPrices | null {
  const parsed = rowSchema.safeParse(row);
  if (!parsed.success) return null;
  const { language, card_id, prices, fetched_at } = parsed.data;
  return {
    language,
    cardId: card_id,
    prices: prices.map(compact),
    fetchedAt: new Date(fetched_at).toISOString(),
  };
}

export function pricesToRow({ language, cardId, prices }: CardRef & { prices: PriceQuote[] }) {
  return { language, card_id: cardId, prices };
}

/** Leaves out the fields a quote doesn't have, like quotes straight from a provider. */
function compact(quote: z.infer<typeof quoteSchema>): PriceQuote {
  return Object.fromEntries(
    Object.entries(quote).filter(([, v]) => v !== undefined),
  ) as unknown as PriceQuote;
}
